import React, { useState } from "react"
import { useLazyQuery, useQuery } from "@apollo/client"
import { Group, User } from "lib/types/generated/graphql-types"
import { showEditSidebar } from "lib/store/services/editSidebar/slice"
import { useDispatch } from "react-redux"
import { Tab, TabList, TabPanel, Tabs } from "react-tabs"

import USERS from "graphql/queries/user/Users"

import PageHeader from "components/Ui/Page/PageHeader"
import PrimaryButton from "components/Ui/Buttons/PrimaryButton"
import { UserTable } from "components/User/UserTable"
import { UpdateUser } from "./UpdateUser"
import { CreateUser } from "./CreateUser"
import { GroupUsers } from "components/User/GroupUsers"

import { ReactComponent as PlusIcon } from "images/icons/plus.svg"
import { ShowMoreWrapper } from "./ShowUsers.styled"
import GROUPS from "../../graphql/queries/user/Groups"
import { handleErrorMessages } from "helpers/errors"
import { WithAuth } from "../../components/Auth"
import { PermissionGroups } from "../../routes/constants"

export const ShowUsers = () => {
  const [editUser, setEditUser] = useState<string>("")
  const [userEmail, setUserEmail] = useState<string>("")
  const [users, setUsers] = useState<User[]>([])
  const [nextToken, setNextToken] = useState<string | null>(null)
  const [groupList, setGroupList] = useState<Group[]>([])

  const [getUsers, { loading: fetchMoreLoading, error: fetchMoreError }] = useLazyQuery(USERS)
  const dispatch = useDispatch()

  const { refetch, error, loading } = useQuery(USERS, {
    variables: { limit: 10 },
    onCompleted: (data) => {
      setUsers(data?.users?.users)
      setNextToken(data?.users?.nextToken)
    }
  })

  const filterNewGroups = groupList
    .filter((group: Group) => !group.name.startsWith("brinkcommerce"))
    .sort((a, b) => a.name.localeCompare(b.name))

  useQuery(GROUPS, {
    variables: { limit: 10 },
    onCompleted: (data) => {
      setGroupList(data.groups.groups)
    },
    onError: (err) => handleErrorMessages(err)
  })

  const fetchMoreUsers = () => {
    getUsers({ variables: { limit: 10, token: nextToken } }).then((result) => {
      setUsers([...users, ...result.data.users.users])
      setNextToken(result?.data?.users?.nextToken)
    })
  }

  return (
    <>
      {!editUser && <CreateUser refetch={refetch} groupList={groupList} />}
      <PageHeader
        title="Users"
        description="Manage users from Brink Commerce for Merchant Portal."
        hasTabs={true}
      >
        <WithAuth requiredGroups={PermissionGroups.users.write}>
          <PrimaryButton type="submit" handleClick={() => dispatch(showEditSidebar())}>
            <PlusIcon />
            Create new user
          </PrimaryButton>
        </WithAuth>
      </PageHeader>
      <Tabs>
        <TabList>
          <Tab>All groups</Tab>
          {filterNewGroups.map((group) => (
            <Tab>{group.name}</Tab>
          ))}
        </TabList>
        <TabPanel>
          {editUser && (
            <UpdateUser
              editUser={editUser}
              setEditUser={setEditUser}
              userEmail={userEmail}
              refetch={refetch}
              groupList={filterNewGroups}
            />
          )}
            <UserTable
              loading={(loading && !nextToken)}
              users={users}
              error={error || fetchMoreError}
              editId={editUser}
              setEditId={setEditUser}
              setUserEmail={setUserEmail}
            />
          {nextToken && (
            <ShowMoreWrapper>
              <PrimaryButton loading={fetchMoreLoading} handleClick={() => fetchMoreUsers()}>
                Show more users
              </PrimaryButton>
            </ShowMoreWrapper>
          )}
        </TabPanel>
        {filterNewGroups.map((group) => (
          <TabPanel>
            <GroupUsers
              groupname={group.name}
              setEditUser={setEditUser}
              editUser={editUser}
              setUserEmail={setUserEmail}
              userEmail={userEmail}
              groupList={groupList}
            />
          </TabPanel>
        ))}
      </Tabs>
    </>
  )
}
